import { BodyLG, BodyMD, BodySM, HeadingXL } from "../../../typography";
import { validateEmail, validateFaxOrPhone } from "../../../util/stringUtil";

import { StandardButton } from "@verse_core/shared_components/buttons/StandardButton";
import { gql } from "../../../__generated__";
import { useMutation } from "@apollo/client";
import { useState } from "react";

export const INSERT_CONTACT_US_LEAD = gql(`
  mutation INSERT_CONTACT_US_LEAD(
    $contactForm: ContactFormInput!
  ) {
    insertContactUsLead(
      contactFormInput: $contactForm
    )
  }
`);

const ContactInput = ({
  label,
  name,
  type,
  value,
  onChange,
  onBlur,
  error,
  required = true,
  dataTestId,
}: {
  label: string;
  name: string;
  type: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  error?: string;
  required?: boolean;
  dataTestId?: string;
}) => {
  return (
    <div className="tw-flex tw-w-full tw-flex-col tw-gap-y-[0.25em]">
      <BodyMD className="tw-my-0 tw-text-white">
        {label}
        {required && " *"}
      </BodyMD>
      <input
        data-testid={dataTestId}
        className={`tw-rounded-[0.25em] tw-border tw-border-solid ${
          error ? "tw-border-red-1" : "tw-border-gray-5"
        } tw-bg-transparent tw-p-[.625em] tw-text-white`}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
      />
      {error && (
        <BodySM className="tw-mb-0 tw-mt-[0.25em] tw-text-red-1">
          {error}
        </BodySM>
      )}
    </div>
  );
};

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  practiceName?: string;
  role?: string;
}

interface TouchedFields {
  firstName: boolean;
  lastName: boolean;
  email: boolean;
  phoneNumber: boolean;
  practiceName: boolean;
  role: boolean;
}

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    practiceName: "",
    role: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<TouchedFields>({
    firstName: false,
    lastName: false,
    email: false,
    phoneNumber: false,
    practiceName: false,
    role: false,
  });
  const [submitContact, { loading }] = useMutation(INSERT_CONTACT_US_LEAD);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateField = (
    name: keyof FormErrors,
    value: string,
  ): string | undefined => {
    if (name === "phoneNumber") {
      if (!value.trim()) {
        return "Phone number is required";
      }
      if (!validateFaxOrPhone(value)) {
        return "Please enter a valid US phone number";
      }
    }
    if (name === "email") {
      if (!value.trim()) {
        return "Email is required";
      }
      if (!validateEmail(value)) {
        return "Please enter a valid email address";
      }
    }
    if (name !== "role" && !value.trim()) {
      return `${
        name.charAt(0).toUpperCase() +
        name
          .slice(1)
          .replace(/([A-Z])/g, " $1")
          .trim()
      } is required`;
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    Object.keys(formData).forEach((key) => {
      const fieldName = key as keyof FormErrors;
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        newErrors[fieldName] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    // Mark all fields as touched when submitting
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      phoneNumber: true,
      practiceName: true,
      role: true,
    });

    if (validateForm()) {
      submitContact({
        variables: {
          contactForm: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phoneNumber: formData.phoneNumber,
            practice: formData.practiceName,
            role: formData.role,
          },
        },
        onCompleted: () => {
          setIsSubmitted(true);
        },
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name as keyof FormErrors, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  if (isSubmitted) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-[2em] tw-rounded-[1em] tw-border-[1px] tw-border-solid tw-border-white tw-border-opacity-20 tw-bg-white tw-bg-opacity-15 tw-p-[2em] tw-text-center">
        <HeadingXL className="tw-my-0 tw-text-white">Thank you!</HeadingXL>
        <BodyLG className="tw-my-0 tw-text-white">
          We've received your request and will be in touch shortly.
        </BodyLG>
      </div>
    );
  }

  const isFormValid =
    formData.firstName.trim() !== "" &&
    formData.lastName.trim() !== "" &&
    formData.email.trim() !== "" &&
    validateEmail(formData.email) &&
    formData.phoneNumber.trim() !== "" &&
    validateFaxOrPhone(formData.phoneNumber) &&
    formData.practiceName.trim() !== "";

  return (
    <div className="tw-flex tw-w-full tw-flex-col tw-gap-y-[2em] tw-rounded-[1em] tw-border-[1px] tw-border-solid tw-border-white tw-border-opacity-20 tw-bg-white tw-bg-opacity-15 tw-p-[2em]">
      <HeadingXL className="tw-my-0 tw-text-white">Book a demo</HeadingXL>
      <div className="tw-flex tw-w-full tw-flex-col tw-gap-x-[1em] tw-gap-y-[2em] min1040:tw-flex-row">
        <ContactInput
          dataTestId="firstName"
          label="First Name"
          name="firstName"
          type="text"
          value={formData.firstName}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.firstName ? errors.firstName : undefined}
        />
        <ContactInput
          dataTestId="lastName"
          label="Last Name"
          name="lastName"
          type="text"
          value={formData.lastName}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.lastName ? errors.lastName : undefined}
        />
      </div>
      <ContactInput
        dataTestId="email"
        label="Email"
        name="email"
        type="email"
        value={formData.email}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.email ? errors.email : undefined}
      />
      <ContactInput
        dataTestId="phone"
        label="Your phone number"
        name="phoneNumber"
        type="tel"
        value={formData.phoneNumber}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.phoneNumber ? errors.phoneNumber : undefined}
      />
      <ContactInput
        dataTestId="nameOfPractice"
        label="Your organization"
        name="practiceName"
        type="text"
        value={formData.practiceName}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.practiceName ? errors.practiceName : undefined}
      />
      <ContactInput
        dataTestId="role"
        label="Your role"
        name="role"
        type="text"
        value={formData.role}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.role ? errors.role : undefined}
        required={false}
      />
      <StandardButton
        style="primary-blue"
        onClick={handleSubmit}
        className="tw-w-full"
        disabled={loading || !isFormValid}
      >
        {loading ? "Submitting..." : "Book a demo"}
      </StandardButton>
    </div>
  );
};
